import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

// The bracket view for the portal and the manager's Display Leaderboard.

const sideShape = PropTypes.shape({
  name: PropTypes.string,
  seed: PropTypes.string,
  score: PropTypes.string,
  affiliation: PropTypes.string,
  winner: PropTypes.bool,
  href: PropTypes.string,
  aggregateId: PropTypes.string,
})

const matchShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  labelHint: PropTypes.string,
  note: PropTypes.string,
  winnerBox: PropTypes.bool,
  side1: sideShape,
  side2: sideShape,
})

const roundShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  matches: PropTypes.arrayOf(matchShape),
  consolation: PropTypes.arrayOf(matchShape),
})

function Side({ side, showSeeds }) {
  const empty = !side || !side.name
  const classes = ['gpb-side']
  if (side && side.winner) classes.push('is-winner')

  const affiliation = (side && side.affiliation) || ''

  return (
    <div className={classes.join(' ')}>
      {showSeeds && <span className='gpb-seed'>{(side && side.seed) || ''}</span>}
      <span className='gpb-lines'>
        {/* data-remote + the class and aggregate id the classic bracket used, so
            rails-ujs and tournaments2's match-detail popup bind to this anchor
            unchanged. Colour comes from the row, not from a blue link. */}
        {!empty && side.href ? (
          <a
            className='gpb-name aggregate_bracket_match'
            href={side.href}
            data-remote='true'
            data-aggregate-id={side.aggregateId}
            data-disable-with={side.name}
          >
            {side.name}
          </a>
        ) : (
          <span className='gpb-name'>{(side && side.name) || ''}</span>
        )}
        {affiliation && <span className='gpb-affiliation'>{affiliation}</span>}
      </span>
      <span className='gpb-score'>{(side && side.score) || ''}</span>
    </div>
  )
}

Side.propTypes = { side: sideShape, showSeeds: PropTypes.bool }

function MatchCard({ match, showSeeds, children }) {
  const below = match.note

  return (
    <div className='gpb-match'>
      {match.label && <div className='gpb-label' title={match.labelHint || undefined}>{match.label}</div>}
      {/* Wrapped so the two sides read as one box: the wrapper owns the radius and
          clips them, which a label or note sibling makes impossible to do with
          :first-child on the sides themselves. */}
      <div className='gpb-sides'>
        <Side side={match.side1} showSeeds={showSeeds} />
        {/* The champion slot has one side; a second row would render as a
            spurious blank opponent. */}
        {!match.winnerBox && <Side side={match.side2} showSeeds={showSeeds} />}
      </div>
      {/* Alongside the tee time, not instead of it: this view runs with starting times
          on for every round, so replacing it would drop information on purpose shown. */}
      {below && <div className='gpb-note'>{below}</div>}
      {children}
    </div>
  )
}

MatchCard.propTypes = {
  match: matchShape.isRequired,
  showSeeds: PropTypes.bool,
  children: PropTypes.node,
}

// An eased rAF loop, because native smooth scrolling varies wildly across browsers.
function animateScrollLeft(el, target, onDone) {
  const start = el.scrollLeft
  const delta = target - start
  if (Math.abs(delta) < 1 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    el.scrollLeft = target
    onDone()
    return function () {}
  }
  // Snap re-snaps mid-write in some engines and judders; we land on a boundary anyway.
  const prevSnap = el.style.scrollSnapType
  el.style.scrollSnapType = 'none'
  const duration = Math.min(820, 320 + Math.abs(delta) * 0.4)
  const startTime = performance.now()
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
  let raf = 0

  function stop() {
    cancelAnimationFrame(raf)
    el.style.scrollSnapType = prevSnap
    el.removeEventListener('wheel', interrupt)
    el.removeEventListener('touchstart', interrupt)
    el.removeEventListener('mousedown', interrupt)
  }
  function interrupt() {
    stop()
    onDone()
  }
  el.addEventListener('wheel', interrupt, { passive: true })
  el.addEventListener('touchstart', interrupt, { passive: true })
  el.addEventListener('mousedown', interrupt)

  const step = (now) => {
    const t = Math.min(1, (now - startTime) / duration)
    el.scrollLeft = start + delta * ease(t)
    if (t < 1) raf = requestAnimationFrame(step)
    else {
      stop()
      onDone()
    }
  }
  raf = requestAnimationFrame(step)
  return stop
}

// Centres the active tab/chip. Assigning scrollLeft at mount is unreliable: the row
// can still be zero-width (hidden container, fonts pending), and the write is a
// no-op while it is display:none, which leaves the active round scrolled offscreen.
function useCentreActive(rowRef, activeIndex) {
  const placed = useRef(false)

  useEffect(() => {
    const row = rowRef.current
    if (!row) return undefined

    const centre = (behavior) => {
      const item = row.children[activeIndex]
      if (!item || !row.clientWidth) return
      row.scrollTo({ left: item.offsetLeft - (row.clientWidth - item.offsetWidth) / 2, behavior })
      placed.current = true
    }

    // Animated only for a change of round: the first placement is a jump.
    centre(placed.current ? 'smooth' : 'auto')
    if (typeof ResizeObserver === 'undefined') return undefined
    // Observing calls back at once, which would jump the row to where the scroll above is
    // already heading and cancel it, so only a width that really changed re-centres: the
    // row being revealed, or the window resized.
    let width = row.clientWidth
    const observer = new ResizeObserver(() => {
      if (row.clientWidth === width) return
      width = row.clientWidth
      centre('auto')
    })
    observer.observe(row)
    return () => observer.disconnect()
  }, [rowRef, activeIndex])
}

function RailColumn({ round, feeds }) {
  const matches = round.matches || []
  return (
    <div className='gpb-col gpb-rail' aria-hidden data-gpb-rail>
      <div className='gpb-col-body'>
        {matches.map((match, i) => {
          // No partner to meet: an elbow would leave its crossbar at the foot of the frame.
          const lone = i === matches.length - 1 && matches.length % 2 === 1
          return (
            <div className='gpb-cell' key={match.id}>
              <span className={`gpb-rail-card${anyWinner(match) ? ' is-decided' : ''}`} />
              {feeds && lone && <span className='gpb-straight' />}
              {feeds && !lone && (
                <span className={`gpb-elbow is-rail ${i % 2 === 0 ? 'is-upper' : 'is-lower'}`} />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

RailColumn.propTypes = { round: roundShape, feeds: PropTypes.bool }

function anyWinner(match) {
  return Boolean((match.side1 || {}).winner || (match.side2 || {}).winner)
}

// Feeder pairs are consecutive matches; a trailing odd one is a bye and gets no connector.
function chunkPairs(items) {
  const pairs = []
  for (let i = 0; i < items.length; i += 2) pairs.push(items.slice(i, i + 2))
  return pairs
}

function DesktopTree({ rounds, showSeeds }) {
  const [activeRound, setActiveRound] = useState(0)
  const scrollRef = useRef(null)
  const tabsRef = useRef(null)
  const programmaticRef = useRef(false)
  const cancelAnimRef = useRef(null)
  // A re-root asked for but not yet committed, so one crossing fires exactly one.
  const pendingRef = useRef(null)
  const prevSLRef = useRef(0)

  useEffect(() => () => cancelAnimRef.current && cancelAnimRef.current(), [])

  // Centres the current round's tab, as the mobile chips do, so each press reveals the
  // rounds after it. The scrollbar is hidden, so this is the only way to reach the last.
  useCentreActive(tabsRef, activeRound)

  const colWidth = () => {
    const el = scrollRef.current
    const col = el && el.querySelector('[data-gpb-col]')
    // Measured, not duplicated: the sass owns the width and widens it for team brackets.
    return col ? col.offsetWidth : 0
  }

  // scrollLeft is absolute (round R at R x COL_W), so a scroll maps on the same both ways.
  const syncFromScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const sl = el.scrollLeft
    const dir = sl - prevSLRef.current
    prevSLRef.current = sl
    if (programmaticRef.current || pendingRef.current !== null) return
    const colW = colWidth()
    if (!colW || dir === 0) return
    const nearest = Math.min(rounds.length - 1, Math.max(0, Math.round(sl / colW)))
    const changed =
      (dir < 0 && nearest < activeRound) || (dir > 0 && nearest > activeRound)
    if (!changed) return
    pendingRef.current = nearest
    setActiveRound(nearest)
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (pendingRef.current === activeRound) {
      pendingRef.current = null
      return
    }
    pendingRef.current = null
    const colW = colWidth()
    if (!colW) return
    const target = activeRound * colW
    if (Math.abs(el.scrollLeft - target) < 2) return
    programmaticRef.current = true
    if (cancelAnimRef.current) cancelAnimRef.current()
    cancelAnimRef.current = animateScrollLeft(el, target, () => {
      programmaticRef.current = false
      prevSLRef.current = el.scrollLeft
    })
  }, [activeRound, rounds.length])

  const rails = rounds.slice(0, activeRound)
  const shown = rounds.slice(activeRound)

  return (
    <div className='gpb-tree-wrap'>
      <nav className='gpb-tabs' aria-label='Rounds' ref={tabsRef}>
        {rounds.map((round, i) => (
          <button
            key={round.id}
            type='button'
            aria-current={i === activeRound ? 'true' : undefined}
            className={`gpb-tab${i === activeRound ? ' is-active' : ''}`}
            onClick={() => setActiveRound(i)}
          >
            {round.name}
          </button>
        ))}
      </nav>

      {/* .gpb-tree is the scroll box; .gpb-tree-inner sizes to content. The
          focused column's cells carry the row pitch, so it defines the height and
          every other column stretches or compresses to it, no JS measurement. */}
      <div className='gpb-tree' ref={scrollRef} onScroll={syncFromScroll} data-gpb-scroller>
        <div
          className='gpb-tree-inner'
          style={{
            '--gpb-consolation-count': Math.max(0, ...shown.map((r) => (r.consolation || []).length)),
          }}
        >
          {rails.map((round, i) => (
            <RailColumn key={round.id} round={round} feeds={i < rounds.length - 1} />
          ))}
          {shown.map((round, shownIndex) => {
            const roundIndex = activeRound + shownIndex
            return (
              <div
                className={`gpb-col${shownIndex === 0 ? ' is-anchor' : ''}`}
                key={round.id}
                data-gpb-col
              >
                <div className='gpb-col-body'>
                  {(round.matches || []).map((match, matchIndex) => {
                    const feeds = roundIndex < rounds.length - 1
                    const last = matchIndex === round.matches.length - 1
                    // A lone match has no partner, so its connector is a straight line.
                    const lone = last && round.matches.length % 2 === 1
                    // Hung off the final's card, not given a cell: nothing feeds a
                    // consolation match, and a cell would move the whole column.
                    const consolation = last ? round.consolation || [] : []
                    return (
                      <div className='gpb-cell' key={match.id}>
                        <MatchCard match={match} showSeeds={showSeeds}>
                          {consolation.length > 0 && (
                            <div className='gpb-consolation'>
                              {consolation.map((extra) => (
                                <MatchCard key={extra.id} match={extra} showSeeds={showSeeds} />
                              ))}
                            </div>
                          )}
                        </MatchCard>
                        {feeds && lone && <span aria-hidden className='gpb-straight' />}
                        {feeds && !lone && (
                          <span
                            aria-hidden
                            className={`gpb-elbow ${matchIndex % 2 === 0 ? 'is-upper' : 'is-lower'}`}
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
          {/* Trailing empty column: without it the last round sits against the
              scroll edge, under the fade, and the winner's card gets clipped. */}
          <div aria-hidden className='gpb-spacer' />
        </div>
      </div>
    </div>
  )
}

DesktopTree.propTypes = { rounds: PropTypes.arrayOf(roundShape), showSeeds: PropTypes.bool }

function defaultRound(rounds) {
  const live = rounds.findIndex((round) =>
    (round.matches || []).some(
      (match) =>
        (match.side1 || {}).name && (match.side2 || {}).name && !match.side1.winner && !match.side2.winner,
    ),
  )
  return live === -1 ? Math.max(rounds.length - 1, 0) : live
}


function RoundPage({ round, showSeeds }) {
  return (
    <section className='gpb-page' aria-label={round.name}>
      {/* A consolation match stands alone: no feeder, so no connector. */}
      {chunkPairs(round.matches || [])
        .concat((round.consolation || []).map((match) => [match]))
        .map((pair, pairIndex) => (
          <div className='gpb-pair' key={pair[0] ? pair[0].id : pairIndex}>
            {pair.map((match, inPair) => (
              <div className={`gpb-pair-item${inPair === 1 ? ' is-second' : ''}`} key={match.id}>
                <MatchCard match={match} showSeeds={showSeeds}>
                  {pair.length === 2 && (
                    <span aria-hidden className={`gpb-half ${inPair === 0 ? 'is-upper' : 'is-lower'}`} />
                  )}
                </MatchCard>
              </div>
            ))}
            {pair.length === 2 && <span aria-hidden className='gpb-feed' />}
          </div>
        ))}
    </section>
  )
}

RoundPage.propTypes = { round: roundShape, showSeeds: PropTypes.bool }

function MobilePager({ rounds, showSeeds }) {
  const [active, setActive] = useState(() => defaultRound(rounds))
  const [back, setBack] = useState(false)
  const chipsRef = useRef(null)
  const touchRef = useRef(null)

  useCentreActive(chipsRef, active)

  // One round on screen: side by side, every round took the height of the largest.
  const show = (index) => {
    const to = Math.max(0, Math.min(rounds.length - 1, index))
    setBack(to < active)
    setActive(to)
  }

  const onTouchStart = (event) => {
    const touch = event.touches[0]
    touchRef.current = { x: touch.clientX, y: touch.clientY }
  }

  const onTouchEnd = (event) => {
    const from = touchRef.current
    touchRef.current = null
    if (!from) return
    const touch = event.changedTouches[0]
    const dx = touch.clientX - from.x
    // Sideways and decisive, or it was a tap or the reader scrolling the page.
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(touch.clientY - from.y)) show(active + (dx < 0 ? 1 : -1))
  }

  return (
    <div className='gpb-pager'>
      <nav className='gpb-chips' aria-label='Rounds' ref={chipsRef}>
        {rounds.map((r, i) => (
          <button
            key={r.id}
            type='button'
            aria-current={i === active ? 'true' : undefined}
            className={`gpb-chip${i === active ? ' is-active' : ''}`}
            onClick={() => show(i)}
          >
            {r.name}
          </button>
        ))}
      </nav>

      <div className={`gpb-track${back ? ' is-back' : ''}`} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <RoundPage key={rounds[active].id} round={rounds[active]} showSeeds={showSeeds} />
      </div>
    </div>
  )
}

MobilePager.propTypes = { rounds: PropTypes.arrayOf(roundShape), showSeeds: PropTypes.bool }

// No title: the container that opens the bracket already names the tournament.
export default function PortalBracketRoot({ rounds, showSeeds, accent, onAccent, onAccentUi }) {
  const safeRounds = (rounds || []).filter((r) => r && (r.matches || []).length > 0)
  if (safeRounds.length === 0) return null

  const hasAffiliations = safeRounds.some((round) =>
    (round.matches || [])
      .concat(round.consolation || [])
      .some((m) => (m.side1 || {}).affiliation || (m.side2 || {}).affiliation),
  )

  const style = {}
  if (accent) style['--gpb-accent'] = accent
  if (onAccent) style['--gpb-on-accent'] = onAccent
  if (onAccentUi) style['--gpb-on-accent-ui'] = onAccentUi

  return (
    <div
      className={`gg-portal-bracket${hasAffiliations ? ' has-affiliations' : ''}`}
      style={style}
    >
      <DesktopTree rounds={safeRounds} showSeeds={showSeeds} />
      <MobilePager rounds={safeRounds} showSeeds={showSeeds} />
    </div>
  )
}

PortalBracketRoot.propTypes = {
  rounds: PropTypes.arrayOf(roundShape),
  showSeeds: PropTypes.bool,
  accent: PropTypes.string,
}

PortalBracketRoot.defaultProps = { showSeeds: true }
